import {
  getOrderDisplayStatus,
  isChewyPharmacistDeclined,
  isChewyRxUsageCountedInFillNumber,
  type RxUsageStateLike,
} from '~/components/dashboard/clients/utils/orderStatusChipUtils'
import type { OrderDisplayStatus } from '~/constants/orderStatusChip'
import { PrescriptionType } from '~/constants/prescription'
import i18n from '~/locales/i18n'
import type {
  Prescription,
  PrescriptionV2,
} from '~/types/entities/prescriptions'

import type {
  GetOrderTableRowOptions,
  OrderTableCardHeader,
  OrderTableContent,
  OrderTableFieldRowOptions,
} from './orderTableTypes'
import {
  appendShippingColumn,
  EMPTY_FIELD_PLACEHOLDER,
  getOrderDate,
  getOrderTableRow,
  getOrderTableSubHeader,
  isInventoryOrder,
  shouldShowOrderAndPrescriptionNumbers,
} from './rxCardUtilities'
import {
  getShippingForUsage,
  type RxUsageWithShipments,
} from './useRxOrderTableShipments'

/** Canonical row input: one table row (including optional shipping and status). */
export interface OrderTableRowInput {
  fillIndex: number
  prescription: Prescription
  /** Per-usage state (Chewy); passed to getOrderDisplayStatus for per-fill chip. */
  rxUsageState?: RxUsageStateLike | null
  shippingPrimary?: string | null
  shippingSecondary?: string | null
  status?: OrderDisplayStatus | null
  totalFills: number
}

const hasShippingValue = (value: string | null | undefined) =>
  value != null && value !== EMPTY_FIELD_PLACEHOLDER

function getShippingColumnCell(row: OrderTableRowInput) {
  if (row.shippingPrimary != null && row.shippingSecondary != null) {
    return [row.shippingPrimary, row.shippingSecondary]
  }
  if (row.shippingPrimary != null) {
    return row.shippingPrimary
  }
  return EMPTY_FIELD_PLACEHOLDER
}

export interface RefillsToOrderTableRowInputsOptions {
  numberRefills?: number | null
  quantityNumbRefills?: number | null
}

/**
 * Converts parent + refills into canonical row inputs (no shipping).
 * Fill index: original = 1, then 2, 3… by order date ascending.
 * Returned array is ordered for display: descending by fill number (newest first).
 */
export function refillsToOrderTableRowInputs(
  rx: Prescription,
  refills: Prescription[],
  options: RefillsToOrderTableRowInputsOptions = {},
): OrderTableRowInput[] {
  const numRefillsAllowed = Number(
    options.quantityNumbRefills ?? options.numberRefills ?? 0,
  )
  const totalFills = Math.max(numRefillsAllowed + 1, refills.length + 1)
  const byOrderDateAsc = [rx, ...refills].sort((a, b) => {
    const dateA = getOrderDate(a) ?? ''
    const dateB = getOrderDate(b) ?? ''
    return dateA.localeCompare(dateB)
  })
  const forDisplay = byOrderDateAsc.slice().reverse()
  return forDisplay.map((prescription, i) => ({
    prescription,
    fillIndex: byOrderDateAsc.length - i,
    totalFills,
    shippingPrimary: null,
    shippingSecondary: null,
  }))
}

export interface RxUsagesWithShipmentsDataForConversion {
  id: string
  rxNo?: string | null
  rxUsages?: Array<RxUsageWithShipments | null> | null
}

/** Order date for a usage (for sorting: oldest first = fill 1). */
function getUsageOrderDate(usage: RxUsageWithShipments | null): string {
  return (usage?.rxOrder?.placedAt as string | undefined) ?? ''
}

/**
 * Converts rxUsages (and shipment data) into canonical row inputs with shipping.
 * Total fills = max(quantityNumbRefills + 1, non-canceled usage count); canceled rows do not add to the denominator.
 * Fill index (numerator): only non-canceled usages get 1…n by order date; canceled rows use fillIndex 0
 * (rendered as "-- / total" in the Chewy order table).
 * Display order: descending (newest first).
 */
export function rxUsagesToOrderTableRowInputs(
  rx: Prescription,
  rxUsagesData: RxUsagesWithShipmentsDataForConversion,
): OrderTableRowInput[] {
  const rawUsages = rxUsagesData.rxUsages ?? []
  const usages = rawUsages.filter((u): u is RxUsageWithShipments => u != null)
  if (usages.length === 0) return []

  const numRefillsAllowed =
    (rx as { quantityNumbRefills?: number | null }).quantityNumbRefills ?? 0
  const byOrderDateAsc = [...usages].sort((a, b) =>
    getUsageOrderDate(a).localeCompare(getUsageOrderDate(b)),
  )
  const usagesForFillIndex = byOrderDateAsc.filter((u) =>
    isChewyRxUsageCountedInFillNumber(u),
  )
  const totalFills = Math.max(numRefillsAllowed + 1, usagesForFillIndex.length)

  const rows = byOrderDateAsc.map((usage) => {
    const countsInFillNumber = isChewyRxUsageCountedInFillNumber(usage)
    const fillIndex = countsInFillNumber
      ? usagesForFillIndex.indexOf(usage) + 1
      : 0
    const { primary: shippingPrimary, secondary: shippingSecondary } =
      countsInFillNumber
        ? getShippingForUsage(usage)
        : { primary: EMPTY_FIELD_PLACEHOLDER, secondary: null }

    const dispensedQty =
      usage?.dispensedAmount != null && usage.dispensedAmount !== ''
        ? Number(usage.dispensedAmount)
        : null
    const syntheticPrescription: Prescription = {
      ...rx,
      id: usage?.externalRxUsageId ?? rx.id,
      creationDate:
        (usage?.rxOrder?.placedAt as string | undefined) ?? rx.creationDate,
      orderNumber: usage?.rxOrder?.externalOrderId ?? rx.orderNumber,
      rxNo: rxUsagesData.rxNo ?? rx.rxNo,
      ...(dispensedQty != null
        ? {
            totalQuantity: dispensedQty,
            quantityPerRefill: dispensedQty,
          }
        : {}),
    } as Prescription

    return {
      prescription: syntheticPrescription,
      fillIndex,
      totalFills,
      rxUsageState: usage?.rxUsageState ?? undefined,
      shippingPrimary,
      shippingSecondary,
    }
  })

  return rows.reverse()
}

export interface OrderTableRowInputsToContentOptions extends OrderTableFieldRowOptions {
  convertedLoadedRx?: Prescription
  isOrderedDrug?: boolean
  loadedSameAsRx?: boolean
  /** When set (e.g. after expanding order table), use so refills and full prescription are available for building rows when not using rxUsages. */
  orderTableLoadedRx?: PrescriptionV2 | null
  parentRx: Prescription
  prescriptionType: PrescriptionType | null | undefined
  /** When set (Chewy) and rxUsages present, table is built from rxUsages instead of refills. */
  rxUsagesData?: RxUsagesWithShipmentsDataForConversion | null
  timezone: string
}

/**
 * Builds OrderTableContent from canonical row inputs.
 * Uses getOrderTableRow for cells, derives card headers from prescription, appends shipping when present.
 */
export function orderTableRowInputsToContent(
  rowInputs: OrderTableRowInput[],
  options: OrderTableRowInputsToContentOptions,
): OrderTableContent {
  const {
    parentRx,
    convertedLoadedRx,
    loadedSameAsRx = false,
    prescriptionType,
    timezone,
    ...opts
  } = options
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const isOrderedDrug = options.isOrderedDrug ?? isInventoryOrder(parentRx)

  const rowInputsWithStatus: OrderTableRowInput[] = rowInputs.map((r) => ({
    ...r,
    status:
      r.status ??
      getOrderDisplayStatus({
        prescription: r.prescription,
        prescriptionStates: opts.prescriptionStates,
        retailOrderStates: opts.retailOrderStates,
        rxUsageState: r.rxUsageState ?? undefined,
      }),
  }))

  const baseRowOptions: Omit<
    GetOrderTableRowOptions,
    'fillIndex' | 'parentForQty' | 'parentPrescription' | 'qtyRx' | 'totalFills'
  > = {
    ...opts,
    isInHouse: isInHouse || isOrderedDrug,
    isOrderedDrug,
    prescriptionType:
      prescriptionType ?? (isOrderedDrug ? PrescriptionType.IN_HOUSE : null),
    timezone,
  }

  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const rows = rowInputsWithStatus.map((rowInput) => {
    const { prescription, fillIndex, totalFills } = rowInput
    const isParentById = prescription.id === parentRx.id
    const isParentRow = isChewy ? fillIndex === 1 : isParentById
    const rowOptions: GetOrderTableRowOptions = {
      ...baseRowOptions,
      fillIndex,
      totalFills,
      parentForQty: isParentRow ? undefined : (convertedLoadedRx ?? parentRx),
      parentPrescription: isParentRow ? undefined : parentRx,
      qtyRx:
        !isChewy && isParentRow && loadedSameAsRx
          ? convertedLoadedRx
          : undefined,
    }
    return getOrderTableRow(prescription, rowOptions)
  })

  const firstRow = rows[0] ?? []
  const headers = firstRow.map((cell) => cell.label)

  const parentHeader = getOrderTableSubHeader(parentRx, prescriptionType)

  const cardHeaders: OrderTableCardHeader[] = rowInputsWithStatus.map(
    (rowInput) => {
      const { prescription, fillIndex } = rowInput
      const status = rowInput.status ?? null
      const isParentById = prescription.id === parentRx.id
      const isParentRow = isChewy ? fillIndex === 1 : isParentById
      const headerPrescriptionType = isParentRow
        ? prescriptionType
        : (
            prescription as Prescription & {
              prescriptionType?: PrescriptionType | null
            }
          ).prescriptionType
      const showOrderAndPrescriptionNumbers =
        shouldShowOrderAndPrescriptionNumbers(headerPrescriptionType)

      if (isParentRow) {
        return {
          text: parentHeader ?? i18n.t('Common:ORDER'),
          status,
          // Chewy: each row’s synthetic prescription carries rx-usage orderNumber (externalOrderId).
          // Prefer that over parentRx, which can be null or stale until synced.
          orderNumber:
            prescription.orderNumber ??
            parentRx.orderNumber ??
            EMPTY_FIELD_PLACEHOLDER,
          rxNo: prescription.rxNo ?? parentRx.rxNo ?? EMPTY_FIELD_PLACEHOLDER,
          ...(showOrderAndPrescriptionNumbers
            ? { showOrderAndPrescriptionNumbers: true }
            : {}),
        }
      }

      const refillHeader = getOrderTableSubHeader(
        prescription,
        (
          prescription as Prescription & {
            prescriptionType?: PrescriptionType | null
          }
        ).prescriptionType,
      )
      const dispensedFromInventory = i18n.t('Common:DISPENSED_FROM_INVENTORY')
      const text =
        refillHeader === dispensedFromInventory
          ? i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.REFILL_DISPENSED')
          : (refillHeader ?? parentHeader ?? i18n.t('Common:ORDER'))

      return {
        text,
        status,
        orderNumber: prescription.orderNumber ?? EMPTY_FIELD_PLACEHOLDER,
        rxNo: prescription.rxNo ?? EMPTY_FIELD_PLACEHOLDER,
        ...(showOrderAndPrescriptionNumbers
          ? { showOrderAndPrescriptionNumbers: true }
          : {}),
      }
    },
  )

  const content: OrderTableContent = {
    cardHeaders,
    headers,
    rows,
  }

  const hasPharmacistDeclinedFill = rowInputsWithStatus.some((row) =>
    isChewyPharmacistDeclined({
      orderDisplayStatus: row.status,
      rxUsageState: row.rxUsageState ?? null,
    }),
  )

  if (hasPharmacistDeclinedFill) {
    return content
  }

  /** Rows not counted in fill number use fillIndex 0 and placeholder shipping — still show Shipping column. */
  const hasChewyRowWithoutFillNumber = rowInputs.some(
    (row) => row.fillIndex === 0,
  )

  if (
    !rowInputs.some(
      (row) =>
        hasShippingValue(row.shippingPrimary) ||
        hasShippingValue(row.shippingSecondary),
    ) &&
    !hasChewyRowWithoutFillNumber
  ) {
    return content
  }

  return appendShippingColumn(
    content,
    rowInputs.map(getShippingColumnCell),
    i18n.t('Common:SHIPPING'),
  )
}
